import { TPriority, TStatus } from "@/core/TicketCore";

export type TLanguages = "pt" | "en" | "de";

export const languages: string[] = ["pt", "en", "de"];
export const defaultLanguage: string = "en";

type TSetupProps =
  | "yourAccount"
  | "changePassword"
  | "preferences"
  | "adminPanel"
  | "archive"
  | "telemetry";

export type TPages =
  | "home"
  | "archive"
  | "new"
  | "ticket"
  | "services"
  | "sla"
  | "analytics"
  | "preferences"
  | "login"
  | "logout"
  | "changepw"
  | "responsibles"
  | "external"
  | "resetpw"
  | "admin"
  | "adminResp"
  | "adminSla"
  | "adminUser"
  | "ticketDataTable"
  | "ai"
  | "status"
  | "sendToBacklog"
  | "adminServices"
  | "forgotpw";

type MetadataTranslations = Partial<
  Record<TPages, Record<string, Record<TLanguages, string>>>
>;

export const monthTranslations: Record<
  number,
  { [language in TLanguages]: string }
> = {
  0: { pt: "Janeiro", en: "January", de: "Januar" },
  1: { pt: "Fevereiro", en: "February", de: "Februar" },
  2: { pt: "Março", en: "March", de: "März" },
  3: { pt: "Abril", en: "April", de: "April" },
  4: { pt: "Maio", en: "May", de: "Mai" },
  5: { pt: "Junho", en: "June", de: "Juni" },
  6: { pt: "Julho", en: "July", de: "Juli" },
  7: { pt: "Agosto", en: "August", de: "August" },
  8: { pt: "Setembro", en: "September", de: "September" },
  9: { pt: "Outubro", en: "October", de: "Oktober" },
  10: { pt: "Novembro", en: "November", de: "November" },
  11: { pt: "Dezembro", en: "December", de: "Dezember" },
  12: { pt: "Total", en: "Total", de: "Gesamt" },
};

// Definir os tipos de tradução para cada página e cada propriedade dentro dela
export type TPageTranslations = {
  [key in TPages]: {
    title: {
      [language in TLanguages]: string; // Garante que 'title' tenha tradução para todos os idiomas
    };
    description: {
      [language in TLanguages]: string; // Garante que 'description' tenha tradução para todos os idiomas
    };
    [key: string]: {
      [language in TLanguages]: string; // Garante que qualquer chave adicional tenha tradução para todos os idiomas
    };
  };
} & {
  navigation: {
    [key in TPages]: {
      [language in TLanguages]: string; // Garante que qualquer chave adicional tenha tradução para todos os idiomas
    };
  };
} & {
  priority: {
    [key in TPriority]: {
      [language in TLanguages]: string;
    };
  };
} & {
  navbar: {
    services: { [language in TLanguages]: string };
    sla: { [language in TLanguages]: string };
    search: { [language in TLanguages]: string };
    environment: { [language in TLanguages]: string };
    setup: {
      [key in TSetupProps]: {
        [language in TLanguages]: string;
      };
    };
  };
} & {
  status: {
    [key in TStatus]: {
      [language in TLanguages]: string;
    };
  };
} & {
  common: {
    [key in string]: {
      [language in TLanguages]: string;
    };
  };
} & {
  metadata: MetadataTranslations;
};

// --------- Objetos comuns para evitar duplicação ---------
// Objetos comuns para evitar duplicação
const commonNav = {
  backToHome: {
    pt: "Voltar para a Página Inicial",
    en: "Back to Home",
    de: "Zur Startseite",
  },
  backToAdminPanel: {
    pt: "Voltar para o Painel do Administrador",
    en: "Back to Admin Panel",
    de: "Zur Admin-Übersicht",
  },
};
// ---------------------------------------------------------

// Exemplo de tradução para cada página com múltiplos objetos de tradução
export const pageTranslations: TPageTranslations = {
  metadata: {
    ticket: {
      about: {
        pt: "Ticket sobre",
        en: "Ticket about",
        de: "Ticket über",
      },
      description: {
        pt: "Clique no link para abrir o ticket.",
        en: "Click the link to open the ticket.",
        de: "Klicken Sie auf den Link, um das Ticket zu öffnen.",
      },
      where: {
        pt: "no Customer Service Desk da GELITA do BRASIL",
        en: "on GELITA do BRASIL Customer Service Desk",
        de: "auf dem Customer Service Desk von GELITA do BRASIL",
      },
    },
  },

  navigation: {
    archive: commonNav.backToHome,
    home: { pt: "", en: "", de: "" },
    new: commonNav.backToHome,
    ai: commonNav.backToHome,
    ticket: commonNav.backToHome,
    services: commonNav.backToHome,
    sla: commonNav.backToHome,
    analytics: commonNav.backToHome,
    preferences: commonNav.backToHome,
    login: { pt: "", en: "", de: "" },
    logout: { pt: "", en: "", de: "" },
    changepw: commonNav.backToHome,
    responsibles: commonNav.backToHome,
    external: commonNav.backToAdminPanel,
    admin: commonNav.backToAdminPanel,
    adminResp: commonNav.backToAdminPanel,
    adminServices: commonNav.backToAdminPanel,
    adminSla: commonNav.backToAdminPanel,
    adminUser: commonNav.backToAdminPanel,
    resetpw: commonNav.backToAdminPanel,
    forgotpw: { pt: "", en: "", de: "" },
    ticketDataTable: { pt: "", en: "", de: "" },
    status: { pt: "", en: "", de: "" },
    sendToBacklog: { pt: "", en: "", de: "" },
  },

  priority: {
    high: { pt: "Alta", en: "High", de: "Hoch" },
    medium: { pt: "Média", en: "Medium", de: "Mittel" },
    low: { pt: "Baixa", en: "Low", de: "Niedrig" },
    "stand up": { pt: "Stand Up COP", en: "Stand Up COP", de: "Stand-Up COP" },
  },

  navbar: {
    services: { pt: "Serviços", en: "Services", de: "Dienstleistungen" },
    sla: { pt: "Tempo de Resposta", en: "Response Time", de: "Antwortzeit" },
    search: {
      pt: "Digite o nº de um ticket",
      en: "Insert a ticket num.",
      de: "Ticketnummer eingeben",
    },
    setup: {
      yourAccount: { pt: "Sua conta", en: "Your account", de: "Ihr Konto" },
      changePassword: {
        pt: "Alterar Senha",
        en: "Change Password",
        de: "Passwort ändern",
      },
      preferences: {
        pt: "Preferências",
        en: "Preferences",
        de: "Einstellungen",
      },
      adminPanel: {
        pt: "Painel do Administrador",
        en: "Admin Panel",
        de: "Administrationsbereich",
      },
      archive: {
        pt: "Tickets arquivados",
        en: "Archived tickets",
        de: "Archivierte Tickets",
      },
      telemetry: {
        pt: "Status do Site",
        en: "Site Status",
        de: "Website-Status",
      },
    },
    environment: {
      pt: "Este é um ambiente de TESTES. NÃO COMPARTILHE informações confidenciais!",
      en: "This is a TEST environment. DO NOT share confidential information!",
      de: "Dies ist eine TEST-Umgebung. Teilen Sie keine vertraulichen Informationen!",
    },
  },

  ai: {
    description: {
      pt: "",
      en: "",
      de: "",
    },
    title: {
      pt: "Resumo com IA",
      en: "Summarize with AI",
      de: "Mit AI zusammenfassen",
    },
    loading: {
      pt: "Resumindo...",
      en: "Summarizing...",
      de: "Wird zusammengefasst...",
    },
    commentsTitle: {
      pt: "Resumo",
      en: "Summary",
      de: "Zusammenfassung",
    },
    error: {
      pt: "Erro",
      en: "Error",
      de: "Fehler",
    },
    generatedOn: {
      pt: "gerado em {timestamp}",
      en: "generated on {timestamp}",
      de: "erstellt am {timestamp}",
    },
    commentsSummaryError: {
      pt: "Erro ao resumir comentários. Tente novamente mais tarde.",
      en: "Failed to summarize comments. Try again later.",
      de: "Fehler beim Zusammenfassen der Kommentare. Bitte versuchen Sie es später erneut.",
    },
    aiDisclaimer: {
      pt: "Este conteúdo foi gerado por IA; confira o resultado e analise informações importantes.",
      en: "This content was generated by AI; please review the result and analyze important information.",
      de: "Dieser Inhalt wurde von KI erstellt; überprüfen Sie das Ergebnis und analysieren Sie wichtige Informationen.",
    },
  },

  home: {
    title: {
      pt: "Customer Service Desk",
      en: "Customer Service Desk",
      de: "Customer Service Desk",
    },
    description: {
      pt: "Página central de solicitações para os times de Supply Chain e Qualidade",
      en: "Central requests page for the Supply Chain and Quality teams",
      de: "Zentrale Anfrageseite für die Supply-Chain- und Qualitätsteams",
    },
  },

  archive: {
    title: {
      pt: "",
      en: "Archived Tickets",
      de: "",
    },
    description: {
      pt: "",
      en: "Historical record of tickets that have been archived. All data is read-only.",
      de: "",
    },
  },

  new: {
    title: {
      pt: "Crie um Ticket",
      en: "Create a Ticket",
      de: "Ticket erstellen",
    },
    description: {
      pt: "Tem uma solicitação para os times de Supply Chain ou para Qualidade? Abra um ticket para uma solução ágil.",
      en: "Do you have a request for the Supply Chain or Quality teams? Open a ticket for a quick solution.",
      de: "Haben Sie eine Anfrage an das Supply-Chain- oder Qualitätsteam? Öffnen Sie ein Ticket für eine schnelle Lösung.",
    },
    // Toast
    ticketCreated: {
      pt: "Ticket criado!",
      en: "Ticket created!",
      de: "Ticket erstellt!",
    },
    viewTicket: { pt: "ver ticket", en: "View Ticket", de: "Ticket anzeigen" },
    copyTicket: {
      pt: "Copiar ticket",
      en: "Copy Ticket",
      de: "Ticket kopieren",
    },

    // Form labels & placeholders
    titleLabel: { pt: "Título", en: "Title", de: "Titel" },
    titlePlaceholder: {
      pt: "Título da sua solicitação",
      en: "Title of your request",
      de: "Titel Ihrer Anfrage",
    },

    poLabel: { pt: "PO", en: "PO", de: "PO" },
    noPO: { pt: "Sem PO", en: "No PO", de: "Ohne PO" },

    descriptionLabel: {
      pt: "Descrição",
      en: "Description",
      de: "Beschreibung",
    },
    descriptionPlaceholder: {
      pt: "Descreva sua solicitação, explicando sua origem, como podemos auxiliar e/ou toda informação suplementar que julgue necessária.",
      en: "Describe your request, explaining its origin, how we can assist, and any extra information you think we need.",
      de: "Beschreiben Sie Ihre Anfrage, erklären Sie ihren Ursprung, wie wir Sie unterstützen können und geben Sie alle zusätzlichen Informationen an, die Sie für wichtig halten.",
    },

    // Submit button
    creatingTicket: {
      pt: "Criando ticket...",
      en: "Creating ticket...",
      de: "Ticket wird erstellt...",
    },
    createTicket: {
      pt: "Criar Ticket",
      en: "Create Ticket",
      de: "Ticket erstellen",
    },

    // Select / Field defaults
    selectPriorityPlaceholder: {
      pt: "Selecione a prioridade",
      en: "Select priority",
      de: "Priorität auswählen",
    },
    selectService: {
      pt: "Selecione um serviço",
      en: "Select a service",
      de: "Dienst auswählen",
    },
    selectPriority: {
      pt: "Selecione uma prioridade",
      en: "Select a priority",
      de: "Priorität auswählen",
    },

    // SLA / Scope info
    responseEstimate: {
      pt: "Estimativa de Resposta",
      en: "Response Estimate",
      de: "Geschätzte Antwortzeit",
    },

    // Responsible
    responsibleLabel: {
      pt: "Responsável",
      en: "Responsible",
      de: "Verantwortlicher",
    },
    defaultResponsibleLabel: {
      pt: "Responsável padrão",
      en: "Default Responsible",
      de: "Standardverantwortlicher",
    },

    // Stakeholders & Attachments
    stakeholdersLabel: {
      pt: "Interessados",
      en: "Stakeholders",
      de: "Stakeholder",
    },
    attachmentsLabel: { pt: "Anexos", en: "Attachments", de: "Anhänge" },
    attachmentsInfo: {
      pt: "(máx 20MB cada)",
      en: "(max 20MB each)",
      de: "(max. 20 MB pro Datei)",
    },

    // validações Zod
    titleMinLength: {
      pt: "Por favor, crie um título com pelo menos 5 caracteres",
      en: "Please create a title with at least 5 characters",
      de: "Bitte erstellen Sie einen Titel mit mindestens 5 Zeichen",
    },
    serviceNotFound: {
      pt: "Serviço não encontrado",
      en: "Service not found",
      de: "Dienst nicht gefunden",
    },
    responsibleNotFound: {
      pt: "Responsável não encontrado",
      en: "Responsible not found",
      de: "Verantwortlicher nicht gefunden",
    },
    poNumOrNoPO: {
      pt: "Informe o número da PO ou selecione 'Sem PO'",
      en: "Provide the PO number or select 'No PO'",
      de: "Geben Sie die PO-Nummer an oder wählen Sie 'Keine PO'",
    },

    // e-mail
    emailTitle: {
      pt: "Novo Ticket",
      en: "New Ticket",
      de: "Neues Ticket",
    },
    emailGreeting: {
      pt: "Olá, {name}",
      en: "Hello, {name}",
      de: "Hallo, {name}",
    },
    simpleEmailGreeting: {
      pt: "Olá,",
      en: "Hello,",
      de: "Hallo,",
    },
    emailIntro: {
      pt: "Um novo ticket foi criado no Customer Service Desk. Aqui estão os detalhes:",
      en: "A new ticket has been created in the Customer Service Desk. Here are the details:",
      de: "Ein neues Ticket wurde im Customer Service Desk erstellt. Hier die Details:",
    },
    emailLabelTicketId: {
      pt: "ID do Ticket",
      en: "Ticket ID",
      de: "Ticket-ID",
    },
    emailViewLink: {
      pt: "Ver Ticket",
      en: "View Ticket",
      de: "Ticket anzeigen",
    },
    orType: { pt: "ou digite", en: "or type", de: "oder eingeben" },
    stakeholdersTypePlaceholder: {
      pt: "Digite os e-mails dos interessados",
      en: "Enter the stakeholders' email addresses",
      de: "Geben Sie die E-Mail-Adressen der Beteiligten ein",
    },
    stakeholdersNotificationDefault: {
      pt: "Os interessados serão notificados via e-mail de TODAS as atualizações neste Ticket.",
      en: "Stakeholders will be notified by email of ALL updates on this Ticket.",
      de: "Interessierte werden per E-Mail über ALLE Aktualisierungen dieses Tickets benachrichtigt.",
    },

    selectUserPlaceholder: {
      pt: "Selecione um usuário",
      en: "Select a user",
      de: "Wählen Sie einen Benutzer aus",
    },
    searchPlaceholder: {
      pt: "Pesquise...",
      en: "Search...",
      de: "Suchen...",
    },
    noUsersMessage: {
      pt: "Nenhum usuário cadastrado",
      en: "No users registered",
      de: "Keine Benutzer registriert",
    },
    userNotFoundMessage: {
      pt: "Usuário não encontrado",
      en: "User not found",
      de: "Benutzer nicht gefunden",
    },
    tbdServiceEmailOnly: {
      pt: "Os serviços TBD são apenas para Tickets enviados por e-mail. Por favor, selecione outro serviço.",
      en: "TBD services are only for tickets submitted by email. Please select another service.",
      de: "TBD-Dienste sind nur für per E-Mail eingereichte Tickets vorgesehen. Bitte wählen Sie einen anderen Service.",
    },
  },

  ticket: {
    toastAttachmentsSaved: {
      pt: "Novo anexo salvo, por favor, atualize a página para vê-lo.",
      en: "New attachment saved, please refresh the page to view it.",
      de: "Neuer Anhang gespeichert, bitte aktualisieren Sie die Seite, um ihn anzuzeigen.",
    },
    notAcceptingComments: {
      pt: 'Este Ticket não aceita mais comentários, pois não está "Em aberto"',
      en: 'This Ticket no longer accepts comments because it is not "Open"',
      de: 'Dieses Ticket akzeptiert keine Kommentare mehr, da es nicht "Offen" ist',
    },
    title: {
      pt: "Ticket",
      en: "Ticket",
      de: "Ticket",
    },
    description: {
      pt: "",
      en: "",
      de: "",
    },
    restrictedMessage: {
      pt: "Ticket restrito.",
      en: "Restricted ticket.",
      de: "Eingeschränktes Ticket.",
    },
    notFoundMessage: {
      pt: "Ticket não encontrado, tem certeza que escolheu um ticket válido?",
      en: "Ticket not found, are you sure you selected a valid ticket?",
      de: "Ticket nicht gefunden. Sind Sie sicher, dass Sie ein gültiges Ticket ausgewählt haben?",
    },
    requestedOn: {
      pt: "solicitado em",
      en: "requested on",
      de: "angefordert am",
    },
    stakeholders: {
      pt: "Interessados",
      en: "Stakeholders",
      de: "Interessierte",
    },
    newTicket: {
      pt: "Novo Ticket",
      en: "New Ticket",
      de: "Neues Ticket",
    },
    ticketLabel: {
      pt: "Ticket",
      en: "Ticket",
      de: "Ticket",
    },

    // Form labels & placeholders
    titlePlaceholder: {
      pt: "Título da solicitação",
      en: "Title of the request",
      de: "Titel der Anfrage",
    },
    poLabel: {
      pt: "PO",
      en: "PO",
      de: "PO",
    },
    requestedByLabel: {
      pt: "Solicitado por",
      en: "Requested by",
      de: "Angefragt von",
    },
    descriptionLabel: {
      pt: "Descrição",
      en: "Description",
      de: "Beschreibung",
    },
    descriptionEmpty: {
      pt: "Nenhuma descrição foi informada",
      en: "No description provided",
      de: "Keine Beschreibung vorhanden",
    },

    // Select/field defaults
    selectService: {
      pt: "Selecione um serviço",
      en: "Select a service",
      de: "Dienst auswählen",
    },

    selectPriority: {
      pt: "Selecione uma prioridade",
      en: "Select a priority",
      de: "Priorität auswählen",
    },

    // Actions
    changeServicePriority: {
      pt: "Alterar Serviço/Prioridade",
      en: "Change Service/Priority",
      de: "Dienst/Priorität ändern",
    },
    updating: {
      pt: "Atualizando...",
      en: "Updating...",
      de: "Wird aktualisiert...",
    },
    saveChanges: {
      pt: "Salvar alterações",
      en: "Save changes",
      de: "Änderungen speichern",
    },
    // Estimate & duration
    responseEstimateLabel: {
      pt: "Estimativa de Resposta",
      en: "Response Estimate",
      de: "Antwortschätzung",
    },

    durationLabel: {
      pt: "Duração atual",
      en: "Current Duration",
      de: "Aktuelle Dauer",
    },

    wasPrefix: { pt: "Era", en: "Was", de: "War" },

    pendingOnlyMessage: {
      pt: "Você só pode alterar a prioridade de um Ticket antes de iniciá-lo (status = Pendente)",
      en: "You can only change a Ticket’s priority before starting it (status = Pending)",
      de: "Sie können die Priorität eines Tickets nur ändern, bevor es gestartet wurde (Status = Ausstehend).",
    },

    updatingPOError: {
      pt: "Ocorreu algum erro ao editar o número da PO ou a informação do Cliente, tente novamente em breve.",
      en: "An error occurred while editing the PO number or Customer information, please try again later.",
      de: "Beim Bearbeiten der PO-Nummer oder der Kundeninformationen ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.",
    },

    // Dialog (justification)
    justificationDescription: {
      pt: "Justifique por que está mudando a PO e/ou o Cliente após o aceite do Ticket e/ou não ser um Ticket enviado via email.",
      en: "Justify why you are changing the PO and/or the Customer after the Ticket has been accepted and/or not being an email‐submitted Ticket.",
      de: "Erklären Sie, warum Sie die PO und/oder den Kunden nach Annahme des Tickets und/oder bei einem nicht per E-Mail übermittelten Ticket ändern.",
    },
    justificationLabel: {
      pt: "Justificativa",
      en: "Justification",
      de: "Begründung",
    },
    poCustomerChangeComment: {
      pt: "Justificativa de mudança de PO e/ou Cliente: {explanation}.",
      en: "Justification for PO and/or Customer change: {explanation}.",
      de: "Begründung für Änderung von PO und/oder Kunde: {explanation}.",
    },
    poCustomerChangeCommentMissing: {
      pt: "Justificativa de mudança de PO e/ou Cliente não informada.",
      en: "Justification for PO and/or Customer change not provided.",
      de: "Begründung für PO- und/oder Kundenänderung nicht angegeben.",
    },
    // SLA Info
    responseTimeTitle: {
      pt: "Tempo de Resposta",
      en: "Response Time",
      de: "Antwortzeit",
    },
    slaGrossTitle: {
      pt: "SLA Gross",
      en: "SLA Gross",
      de: "Brutto-SLA",
    },
    slaGrossDescription: {
      pt: "O tempo de resposta total: este é o tempo que o cliente demorou para ter seu problema resolvido.",
      en: "Total response time: this is the time the customer took to have their issue resolved.",
      de: "Gesamte Antwortzeit: Dies ist die Zeit, die der Kunde für die Lösung seines Problems benötigt hat.",
    },
    slaNetTitle: {
      pt: "SLA Net",
      en: "SLA Net",
      de: "Netto-SLA",
    },
    slaNetDescription: {
      pt: "Este é o tempo que COP agiu sobre o Ticket, ou seja, desconta o tempo em Backlog.",
      en: "This is the time COP acted on the Ticket, i.e., subtracting time in Backlog.",
      de: "Dies ist die Zeit, in der COP am Ticket gearbeitet hat, d.h. abzüglich der Zeit im Backlog.",
    },

    actionsButton: {
      pt: "Ações",
      en: "Actions",
      de: "Aktionen",
    },
    availableActionsTitle: {
      pt: "Ações disponíveis",
      en: "Available actions",
      de: "Verfügbare Aktionen",
    },
    availableActionsDescription: {
      pt: "Este ticket foi atribuído à você, veja as ações disponíveis.",
      en: "This ticket has been assigned to you; see the available actions.",
      de: "Dieses Ticket wurde Ihnen zugewiesen; sehen Sie die verfügbaren Aktionen.",
    },

    serviceLabel: {
      pt: "Serviço",
      en: "Service",
      de: "Dienst",
    },

    removeFromBacklogButton: {
      pt: "Tirar do Backlog",
      en: "Remove from Backlog",
      de: "Aus dem Backlog entfernen",
    },
    removeFromBacklogPrompt: {
      pt: "Tem certeza? O SLA Total do Ticket voltará a ser contado.",
      en: "Are you sure? The Ticket’s total SLA will start being counted again.",
      de: "Sind Sie sicher? Das gesamte SLA des Tickets wird wieder gezählt.",
    },

    addCommentLabel: {
      pt: "Adicione um comentário",
      en: "Add a comment",
      de: "Kommentar hinzufügen",
    },
    updatingStatus: {
      pt: "Atualizando status...",
      en: "Updating status...",
      de: "Status wird aktualisiert...",
    },
    backlogButton: {
      pt: "Backlog",
      en: "Backlog",
      de: "Backlog",
    },
    selectBacklogInstruction: {
      pt: "Selecione a área do Backlog e o novo responsável pelo Ticket.",
      en: "Select the Backlog team and the new responsible person for the Ticket.",
      de: "Wählen Sie das Backlog-Team und die neue verantwortliche Person für das Ticket aus.",
    },
    selectBacklogAreaLabel: {
      pt: "Selecione a área responsável",
      en: "Select the responsible team",
      de: "Verantwortliches Team auswählen",
    },
    selectBacklogResponsibleLabel: {
      pt: "Selecione a pessoa responsável (opcional)",
      en: "Select the responsible person (optional)",
      de: "Verantwortliche Person auswählen (optional)",
    },
    sendToBacklogButton: {
      pt: "Enviar para Backlog",
      en: "Send to Backlog",
      de: "In Backlog senden",
    },
    informBacklogAreaAlert: {
      pt: "Informe a área responsável pelo Backlog",
      en: "Please specify the team responsible for Backlog",
      de: "Bitte geben Sie das für das Backlog zuständige Team an.",
    },

    // Accept Ticket Dialog
    acceptTicketTrigger: {
      pt: "Iniciar Ticket",
      en: "Start Ticket",
      de: "Ticket starten",
    },
    acceptTicketTitle: {
      pt: "Aceitar ticket",
      en: "Accept Ticket",
      de: "Ticket annehmen",
    },
    acceptTicketDescription: {
      pt: "Este ticket foi atribuído à você, veja um resumo abaixo e dê início ao processo.",
      en: "This ticket has been assigned to you; see a summary below and start the process.",
      de: "Dieses Ticket wurde Ihnen zugewiesen; sehen Sie die Zusammenfassung unten und starten Sie den Prozess.",
    },
    openingCommentLabel: {
      pt: "Adicione um comentário de abertura",
      en: "Add an opening comment",
      de: "Eröffnungs-Kommentar hinzufügen",
    },
    confirmAcceptTicket: {
      pt: "Aceitar Ticket",
      en: "Accept Ticket",
      de: "Ticket annehmen",
    },
    acceptingTicket: {
      pt: "Aceitando Ticket...",
      en: "Accepting Ticket...",
      de: "Ticket wird angenommen...",
    },
    tbdServiceAlert: {
      pt: "Por favor, antes de iniciar o Ticket, selecione um serviço diferente de TDB",
      en: "Please, before starting the Ticket, select a service other than TBD",
      de: "Bitte wählen Sie vor dem Start des Tickets einen anderen Dienst als TBD aus.",
    },

    // End Ticket Dialog
    endTicketTrigger: {
      pt: "Encerrar",
      en: "Close",
      de: "Abschließen",
    },
    endTicketPrompt: {
      pt: "Tem certeza que quer encerrar o ticket?",
      en: "Are you sure you want to close the ticket?",
      de: "Sind Sie sicher, dass Sie das Ticket schließen möchten?",
    },
    endCommentLabel: {
      pt: "Adicione um comentário de encerramento do Ticket",
      en: "Add a closing comment for the Ticket",
      de: "Schlusskommentar für das Ticket hinzufügen",
    },
    confirmEndTicket: {
      pt: "Encerrar Ticket",
      en: "Close Ticket",
      de: "Ticket schließen",
    },
    endingTicket: {
      pt: "Encerrando...",
      en: "Closing...",
      de: "Wird geschlossen...",
    },

    // Reject Ticket Dialog
    rejectTicketTitle: {
      pt: "Rejeitar Ticket",
      en: "Reject Ticket",
      de: "Ticket ablehnen",
    },
    rejectTicketDescription: {
      pt: "Tem certeza que deseja rejeitar esse Ticket?",
      en: "Are you sure you want to reject this Ticket?",
      de: "Sind Sie sicher, dass Sie dieses Ticket ablehnen möchten?",
    },
    confirmRejectLabel: {
      pt: "Digite 'rejeitar' para confirmar esta ação",
      en: "Type 'rejeitar' to confirm this action",
      de: "Geben Sie 'rejeitar' ein, um diese Aktion zu bestätigen",
    },
    rejectCommentLabel: {
      pt: "Adicione um comentário explicando a Rejeição do Ticket.",
      en: "Add a comment explaining the Ticket rejection.",
      de: "Fügen Sie einen Kommentar hinzu, der die Ticket-Ablehnung erklärt.",
    },
    confirmRejectTicket: {
      pt: "Rejeitar",
      en: "Reject",
      de: "Ablehnen",
    },
    rejectingTicket: {
      pt: "Rejeitando Ticket...",
      en: "Rejecting Ticket...",
      de: "Ticket wird abgelehnt...",
    },

    // Reopen Ticket Dialog
    reopenTicketTrigger: {
      pt: "Reabrir",
      en: "Reopen",
      de: "Wieder öffnen",
    },
    reopenTicketTitle: {
      pt: "Reabrir Ticket?",
      en: "Reopen Ticket?",
      de: "Ticket erneut öffnen?",
    },
    reopenTicketDescription: {
      pt: "Se você entende que esse ticket não foi finalizado corretamente, reabra-o.",
      en: "If you believe this ticket was not properly Abgeschlossen, reopen it.",
      de: "Wenn Sie der Meinung sind, dass dieses Ticket nicht korrekt abgeschlossen wurde, öffnen Sie es erneut.",
    },
    reopenCommentLabel: {
      pt: "Comentário",
      en: "Comment",
      de: "Kommentar",
    },
    reopenPlaceholder: {
      pt: "Descreva o motivo da reabertura deste Ticket.",
      en: "Describe the reason for reopening this Ticket.",
      de: "Beschreiben Sie den Grund für die erneute Öffnung dieses Tickets.",
    },
    confirmReopenTicket: {
      pt: "Reabrir Ticket",
      en: "Reopen Ticket",
      de: "Ticket erneut öffnen",
    },
    reopeningTicket: {
      pt: "Reabrindo...",
      en: "Reopening...",
      de: "Wird erneut geöffnet...",
    },

    commentsTitle: {
      pt: "Comentários",
      en: "Comments",
      de: "Kommentare",
    },
    commentError: {
      pt: "Erro ao comentar",
      en: "Error commenting",
      de: "Fehler beim Kommentieren",
    },
    noCommentsMessage: {
      pt: "Nenhum comentário ainda",
      en: "No comments yet",
      de: "Noch keine Kommentare",
    },
    creatorBadge: {
      pt: "Criador do Ticket",
      en: "Ticket Creator",
      de: "Ticket-Ersteller",
    },
    responsibleBadge: {
      pt: "Responsável pelo Ticket",
      en: "Ticket Responsible",
      de: "Verantwortlicher für das Ticket",
    },

    // Retrieve From Backlog Dialog
    actions: {
      pt: "Ações",
      en: "Actions",
      de: "Aktionen",
    },
    removeFromBacklog: {
      pt: "Tirar do Backlog",
      en: "Remove from Backlog",
      de: "Aus dem Backlog entfernen",
    },
    removeFromBacklogConfirm: {
      pt: "Tem certeza? O SLA Total do Ticket voltará a ser contado.",
      en: "Are you sure? The Ticket’s total SLA will start being counted again.",
      de: "Sind Sie sicher? Das gesamte SLA des Tickets wird wieder gezählt.",
    },

    // Send To Backlog Dialog
    backlogTrigger: {
      pt: "Backlog",
      en: "Backlog",
      de: "Backlog",
    },
    sendToBacklog: {
      pt: "Enviar para Backlog",
      en: "Send to Backlog",
      de: "In Backlog senden",
    },

    // Accept Ticket Dialog (duplicate keys)
    acceptTrigger: {
      pt: "Iniciar Ticket",
      en: "Start Ticket",
      de: "Ticket starten",
    },
    acceptTitle: {
      pt: "Aceitar ticket",
      en: "Accept Ticket",
      de: "Ticket annehmen",
    },
    acceptDescription: {
      pt: "Este ticket foi atribuído à você, veja um resumo abaixo e dê início ao processo.",
      en: "This ticket has been assigned to you; see a summary below and start the process.",
      de: "Dieses Ticket wurde Ihnen zugewiesen; sehen Sie die Zusammenfassung unten und starten Sie den Prozess.",
    },
    confirmAccept: {
      pt: "Aceitar Ticket",
      en: "Accept Ticket",
      de: "Ticket annehmen",
    },

    // Start ticket when commenting
    startOnCommentTitle: {
      pt: "Iniciar ticket?",
      en: "Start ticket?",
      de: "Ticket starten?",
    },
    startOnCommentDesc: {
      pt: "Este ticket ainda não foi iniciado. Deseja iniciá-lo antes de enviar o seu comentário?",
      en: "This ticket hasn't been started yet. Do you want to start it before sending your comment?",
      de: "Dieses Ticket wurde noch nicht gestartet. Möchten Sie es starten, bevor Sie Ihren Kommentar senden?",
    },
    startOnCommentTbdDesc: {
      pt: "Este ticket ainda não foi iniciado e o serviço está como TBD. Selecione um serviço válido para iniciá-lo antes de enviar o seu comentário.",
      en: "This ticket hasn't been started yet and its service is still TBD. Select a valid service to start it before sending your comment.",
      de: "Dieses Ticket wurde noch nicht gestartet und der Service ist noch TBD. Wählen Sie einen gültigen Service aus, um es zu starten, bevor Sie Ihren Kommentar senden.",
    },
    startAndSend: {
      pt: "Iniciar e enviar",
      en: "Start & send",
      de: "Starten & senden",
    },
    justSendComment: {
      pt: "Apenas enviar",
      en: "Just send",
      de: "Nur senden",
    },

    // End Ticket Dialog (duplicate keys)
    endTrigger: {
      pt: "Encerrar",
      en: "Close",
      de: "Abschließen",
    },
    endConfirm: {
      pt: "Tem certeza que quer encerrar o ticket?",
      en: "Are you sure you want to close the ticket?",
      de: "Sind Sie sicher, dass Sie das Ticket schließen möchten?",
    },

    // Reject Ticket Dialog
    rejectTitle: {
      pt: "Rejeitar Ticket",
      en: "Reject Ticket",
      de: "Ticket ablehnen",
    },
    rejectDescription: {
      pt: "Tem certeza que deseja rejeitar esse Ticket?",
      en: "Are you sure you want to reject this Ticket?",
      de: "Sind Sie sicher, dass Sie dieses Ticket ablehnen möchten?",
    },
    confirmReject: {
      pt: "Rejeitar",
      en: "Reject",
      de: "Ablehnen",
    },

    // Reopen Ticket Dialog (duplicate keys)
    reopenTrigger: {
      pt: "Reabrir",
      en: "Reopen",
      de: "Wieder öffnen",
    },

    reopenTitle: {
      pt: "Reabrir Ticket?",
      en: "Reopen Ticket?",
      de: "Ticket erneut öffnen?",
    },

    reopenDescription: {
      pt: "Se você entende que esse ticket não foi finalizado corretamente, reabra-o.",
      en: "If you believe this ticket was not properly closed, reopen it.",
      de: "Wenn Sie der Meinung sind, dass dieses Ticket nicht korrekt abgeschlossen wurde, öffnen Sie es erneut.",
    },
    confirmReopen: {
      pt: "Reabrir Ticket",
      en: "Reopen Ticket",
      de: "Ticket erneut öffnen",
    },

    // Responsibilities Section
    responsibilityTitle: {
      pt: "Responsabilidade",
      en: "Responsibility",
      de: "Verantwortung",
    },
    currentResponsibleLabel: {
      pt: "Responsável atual",
      en: "Current Responsible",
      de: "Aktuell Verantwortlicher",
    },
    currAreaLabel: {
      pt: "Área:",
      en: "Department:",
      de: "Bereich:",
    },
    newResponsibleLabel: {
      pt: "Novo responsável",
      en: "New Responsible",
      de: "Neuer Verantwortlicher",
    },
    changeResponsible: {
      pt: "Trocar responsável",
      en: "Change responsible",
      de: "Verantwortlichen ändern",
    },
    notifyNewResponsible: {
      pt: "Notificar novo responsável",
      en: "Notify new responsible",
      de: "Neuen Verantwortlichen benachrichtigen",
    },
    newResponsibleBacklog: {
      pt: "Novo responsável & Backlog",
      en: "New Responsible & Backlog",
      de: "Neuer Verantwortlicher & Backlog",
    },
    notifyingNewResponsible: {
      pt: "Notificando novo responsável...",
      en: "Notifying new responsible...",
      de: "Benachrichtige neuen Verantwortlichen...",
    },
    errorChangeResponsible: {
      pt: "Erro ao trocar responsável, tente novamente em breve",
      en: "Error changing responsible, please try again later",
      de: "Fehler beim Ändern des Verantwortlichen. Bitte versuchen Sie es später erneut.",
    },
    userAssumeLabel: {
      pt: "Deseja assumir este ticket?",
      en: "Do you want to take over this ticket?",
      de: "Möchten Sie dieses Ticket übernehmen?",
    },
    assumeTrigger: {
      pt: "Assumir Ticket",
      en: "Assume Ticket",
      de: "Ticket übernehmen",
    },
    assumeTitle: {
      pt: "Assumir Ticket",
      en: "Assume Ticket",
      de: "Ticket übernehmen",
    },
    assumeDescription: {
      pt: "Tem certeza que deseja assumir esse Ticket?",
      en: "Are you sure you want to assume this Ticket?",
      de: "Sind Sie sicher, dass Sie dieses Ticket übernehmen möchten?",
    },
    confirmAssumeLabel: {
      pt: "Digite 'assumir' para confirmar esta ação",
      en: "Type 'assumir' to confirm this action",
      de: "Geben Sie 'assumir' ein, um diese Aktion zu bestätigen",
    },
    confirmAssume: {
      pt: "Assumir Ticket",
      en: "Assume Ticket",
      de: "Ticket übernehmen",
    },
    assumingTicket: {
      pt: "Assumindo ticket...",
      en: "Assuming ticket...",
      de: "Ticket wird übernommen...",
    },

    // Responsible Selector Popover
    selectResponsiblePlaceholder: {
      pt: "Selecione um responsável",
      en: "Select a responsible",
      de: "Verantwortlichen auswählen",
    },
    searchPlaceholder: {
      pt: "Pesquise...",
      en: "Search...",
      de: "Suchen...",
    },
    noResponsibles: {
      pt: "Nenhum responsável cadastrado",
      en: "No responsibles registered",
      de: "Keine Verantwortlichen registriert",
    },
    responsibleNotFound: {
      pt: "Responsável não encontrado",
      en: "Responsible not found",
      de: "Verantwortlicher nicht gefunden",
    },

    // Attention Dialog
    attentionTitle: {
      pt: "Chamar a atenção",
      en: "Get attention",
      de: "Aufmerksamkeit",
    },
    attentionDescription: {
      pt: "Avise aos responsáveis deste Ticket que você está solicitando atenção extra a demanda.",
      en: "Notify the ticket responsibles that you are requesting extra attention on this demand.",
      de: "Benachrichtigen Sie die Verantwortlichen dieses Tickets, dass Sie zusätzliche Aufmerksamkeit für diese Anfrage wünschen.",
    },
    attentionCommentLabel: {
      pt: "Comentário (obrigatório)",
      en: "Comment (required)",
      de: "Kommentar (erforderlich)",
    },
    attentionPlaceholder: {
      pt: "Informe o motivo deste pedido de atenção.",
      en: "Provide the reason for this attention request.",
      de: "Geben Sie den Grund für diese Aufmerksamkeitsanforderung an.",
    },
    attentionNotificationToLabel: {
      pt: "Notificação para",
      en: "Notification to",
      de: "Benachrichtigung an",
    },
    attentionMainResponsibleLabel: {
      pt: "Responsável padrão",
      en: "Main responsible",
      de: "Hauptverantwortlicher",
    },
    attentionOthersLabel: {
      pt: "Outros interessados",
      en: "Other interested parties",
      de: "Andere Interessierte",
    },
    attentionWarning: {
      pt: "Escolha quem vai receber esta notificação.",
      en: "Choose who will receive this notification.",
      de: "Wählen Sie aus, wer diese Benachrichtigung erhalten soll.",
    },
    attentionErrorToastTitle: {
      pt: "Erro ao pedir atenção",
      en: "Error requesting attention",
      de: "Fehler beim Anfordern von Aufmerksamkeit",
    },
    attentionErrorToastDescription: {
      pt: "Por favor, tente novamente em breve.",
      en: "Please try again later.",
      de: "Bitte versuchen Sie es später erneut.",
    },
    attentionSuccessToastTitle: {
      pt: "Pedido de atenção enviado",
      en: "Attention request sent",
      de: "Aufmerksamkeitsanforderung gesendet",
    },
    attentionSuccessToastDescription: {
      pt: "Os responsáveis foram notificados.",
      en: "The responsibles have been notified.",
      de: "Die Verantwortlichen wurden benachrichtigt.",
    },

    // Admin Edit Dialog
    adminEditDescription: {
      pt: "Como administrador(a) você pode editar alguns campos do Ticket",
      en: "As an administrator you can edit some fields of the Ticket",
      de: "Als Administrator können Sie einige Felder des Tickets bearbeiten",
    },
    adminEditTitleLabel: {
      pt: "Título",
      en: "Title",
      de: "Titel",
    },
    adminEditPriorityLabel: {
      pt: "Prioridade",
      en: "Priority",
      de: "Priorität",
    },
    adminEditPlaceholderPriority: {
      pt: "Selecione a prioridade",
      en: "Select priority",
      de: "Priorität auswählen",
    },
    adminEditServiceLabel: {
      pt: "Serviço",
      en: "Service",
      de: "Dienst",
    },
    adminEditCommentLabel: {
      pt: "Adicione um comentário explicando as alterações feitas.",
      en: "Add a comment explaining the changes made.",
      de: "Fügen Sie einen Kommentar hinzu, der die vorgenommenen Änderungen erklärt.",
    },
    adminEditCommentPlaceholder: {
      pt: "Descreva ou explique as mudanças efetuadas.",
      en: "Describe or explain the changes made.",
      de: "Beschreiben oder erklären Sie die vorgenommenen Änderungen.",
    },
    viewGrid: {
      pt: "Grade",
      en: "Grid",
      de: "Raster",
    },
    viewList: {
      pt: "Lista",
      en: "List",
      de: "Liste",
    },
    downloadAllStatements: {
      pt: "Baixar todos",
      en: "Download all",
      de: "Alle herunterladen",
    },
    downloadingStatements: {
      pt: "Baixando...",
      en: "Downloading...",
      de: "Kontoauszüge werden heruntergeladen...",
    },
    downloadStatementsSuccess: {
      pt: "Download concluído",
      en: "Download completed",
      de: "Download abgeschlossen",
    },
    downloadStatementsError: {
      pt: "Não foi possível baixar os anexos",
      en: "Could not download attachments",
      de: "Konnten nicht heruntergeladen werden",
    },
    addMoreAttachments: {
      pt: "Adicione mais Anexos",
      en: "Add more attachments",
      de: "Weitere Anhänge hinzufügen",
    },
    addAttachments: {
      pt: "Adicionar Anexos",
      en: "Add Attachments",
      de: "Anhänge hinzufügen",
    },
    attaching: {
      pt: "Anexando...",
      en: "Attaching...",
      de: "Anhänge werden hinzugefügt...",
    },
    ticketFinished: {
      pt: "Ticket finalizado",
      en: "Ticket finished",
      de: "Ticket abgeschlossen",
    },
    timeline: {
      pt: "Linha do tempo",
      en: "Timeline",
      de: "Zeitachse",
    },
    by: {
      pt: "por ",
      en: "by ",
      de: "von ",
    },
  },

  services: {
    title: {
      pt: "Serviços",
      en: "Services",
      de: "Dienstleistungen",
    },
    description: {
      pt: "Nossos serviços oferecidos",
      en: "Our offered services",
      de: "Unsere angebotenen Dienstleistungen",
    },
  },

  sla: {
    title: {
      pt: "Estimativa de Resposta",
      en: "Response Estimate",
      de: "Antwortschätzung",
    },
    description: {
      pt: "Veja o SLA e responsável padrão para cada serviço",
      en: "See the default SLA and responsible party for each service",
      de: "Sehen Sie für jeden Dienst das Standard-SLA und die zuständige Stelle",
    },

    slaHours: {
      pt: "SLA (horas)",
      en: "SLA (hours)",
      de: "SLA (Stunden)",
    },

    defaultResponsible: {
      pt: "Responsável Padrão",
      en: "Default Responsible",
      de: "Standardverantwortlicher",
    },

    inactiveSince: {
      pt: "Inativo desde",
      en: "Inactive since",
      de: "Inaktiv seit",
    },
  },

  analytics: {
    title: {
      pt: "Analytics",
      en: "Analytics",
      de: "Analytics",
    },
    description: {
      pt: "Veja a Performance dos nossos serviços! Esta página é atualizada a cada 24 horas.",
      en: "See the performance of our services! This page is updated every 24h.",
      de: "Sehen Sie die Performance unserer Services! Diese Seite wird alle 24 Stunden aktualisiert.",
    },
    scrollSpyTickets: {
      pt: "Tickets",
      en: "Tickets",
      de: "Tickets",
    },
    scrollSpyResponseTime: {
      pt: "Tempo de resposta",
      en: "Response Time",
      de: "Antwortzeit",
    },
    scrollSpyCustomers: {
      pt: "Clientes",
      en: "Customers",
      de: "Kunden",
    },
    scrollSpyServices: {
      pt: "Servi\u00e7os",
      en: "Services",
      de: "Services",
    },
    scrollSpyBacklog: {
      pt: "Backlog",
      en: "Backlog",
      de: "Backlog",
    },
    scrollSpyGoToSection: {
      pt: "Ir para {section}",
      en: "Go to {section}",
      de: "Zu {section} gehen",
    },
    ticketsByMonthTitle: {
      pt: "Tickets por Mês",
      en: "Tickets by Month",
      de: "Tickets pro Monat",
    },
    selectYearPlaceholder: {
      pt: "Selecione o ano",
      en: "Select year",
      de: "Jahr auswählen",
    },
    descriptionYear: {
      pt: "no Ano",
      en: "in Year",
      de: "im Jahr",
    },
    ticketsLegendDefinition: {
      pt: "Abertos são tickets não cancelados; Concluídos são tickets com status = 'concluído'",
      en: "Open are tickets not canceled; Closed are tickets with status = 'closed'",
      de: "Offen sind nicht stornierte Tickets; Geschlossen sind Tickets mit Status = 'Abgeschlossen'",
    },
    ticketsFooterNote: {
      pt: "Usa-se a data de criação para os tickets Abertos, e a data de encerramento para os tickets Concluídos",
      en: "Creation date is used for Open tickets, and closing date for Closed tickets",
      de: "Für offene Tickets wird das Erstellungsdatum verwendet, für geschlossene Tickets das Abschlussdatum.",
    },
    responseTimeTitle: {
      pt: "Tempo de Resposta em horas",
      en: "Response Time in hours",
      de: "Antwortzeit in Stunden",
    },
    customerView: {
      pt: "Clientes",
      en: "Customers",
      de: "Kunde",
    },
    descriptionCustomer: {
      pt: "Top 10",
      en: "Top 10",
      de: "Top 10",
    },
    customerTitleTickets: {
      pt: "#Tickets",
      en: "#Tickets",
      de: "#Tickets",
    },
    customerTitleResponseTime: {
      pt: "Tempo de Resposta",
      en: "Response Time",
      de: "Antwortzeit",
    },
    responseTimeFooterNote: {
      pt: "Mostrando o tempo de resolução médio para fechar um ticket (status = 'concluído')",
      en: "Showing average resolution time to close a ticket (status = 'closed')",
      de: "Zeigt die durchschnittliche Lösungszeit zum Schließen eines Tickets (Status = 'Abgeschlossen') an.",
    },
    responseTimeChangeHigher: {
      pt: "Tempo de resposta está {change}h maior que no mês passado",
      en: "Response time is {change}h higher than last month",
      de: "Die Antwortzeit ist um {change}h höher als im letzten Monat",
    },
    responseTimeChangeLower: {
      pt: "Tempo de resposta está {change}h menor que no mês passado",
      en: "Response time is {change}h lower than last month",
      de: "Die Antwortzeit ist um {change}h niedriger als im letzten Monat",
    },
    aboveAverage: {
      pt: "{hours}h acima da média",
      en: "{hours}h above average",
      de: "{hours}h über dem Durchschnitt",
    },
    belowAverage: {
      pt: "{hours}h abaixo da média",
      en: "{hours}h below average",
      de: "{hours}h unter dem Durchschnitt",
    },
    byServiceTitle: {
      pt: "Tempo de Resposta em horas por Serviço",
      en: "Response Time in hours by Service",
      de: "Antwortzeit in Stunden pro Service",
    },
    byServiceFastest: {
      pt: "Serviço mais rápido: {service}",
      en: "Fastest service: {service}",
      de: "Schnellster Service: {service}",
    },
    byServiceSlowest: {
      pt: "Serviço mais lento: {service}",
      en: "Slowest service: {service}",
      de: "Langsamster Service: {service}",
    },
    byServiceFooterNote: {
      pt: "Tempo de resposta em HORAS por serviço (status = 'concluído')",
      en: "Response time in HOURS per service (status = 'closed')",
      de: "Antwortzeit in STUNDEN pro Service (Status = 'Abgeschlossen')",
    },
    backlogTitle: {
      pt: "Backlog",
      en: "Backlog",
      de: "Backlog",
    },
    backlogFooterNote: {
      pt: "Tempo de resposta em HORAS por área para cada solicitação de Backlog. O Escopo é definido com base no último serviço do Ticket.",
      en: "Response time in HOURS by area for each Backlog request. The Scope is defined based on the Ticket's last service.",
      de: "Antwortzeit in STUNDEN pro Bereich für jede Backlog-Anfrage. Der Umfang wird basierend auf dem letzten Service des Tickets festgelegt.",
    },

    filters: {
      pt: "Filtros",
      en: "Filters",
      de: "Filter",
    },
    allDepartments: {
      pt: "Todos os departamentos",
      en: "All departments",
      de: "Alle Abteilungen",
    },
    selectedDepartments: {
      pt: "{count} departamentos",
      en: "{count} departments",
      de: "{count} Abteilungen",
    },
    searchDepartment: {
      pt: "Buscar departamento...",
      en: "Search department...",
      de: "Abteilung suchen...",
    },
    noDepartment: {
      pt: "Nenhum departamento encontrado",
      en: "No department found",
      de: "Keine Abteilung gefunden",
    },

    openTickets: {
      pt: "Tickets abertos",
      en: "Open Tickets",
      de: "Offene Tickets",
    },
    overdueTickets: {
      pt: "Tickets atrasados",
      en: "Overdue Tickets",
      de: "Überfällige Tickets",
    },
    averageResponse: {
      pt: "Média de resposta",
      en: "Average Response",
      de: "Durchschnittliche Antwortzeit",
    },
    openedSupply: {
      pt: "Abertos - Supply",
      en: "Opened - Supply",
      de: "Geöffnet - Supply",
    },
    closedSupply: {
      pt: "Concluídos - Supply",
      en: "Closed - Supply",
      de: "Geschlossen - Supply",
    },
    openedQRA: {
      pt: "Abertos - QRA",
      en: "Opened - QRA",
      de: "Geöffnet - QRA",
    },
    closedQRA: {
      pt: "Concluídos - QRA",
      en: "Closed - QRA",
      de: "Geschlossen - QRA",
    },
    avgAll: {
      pt: "Média geral",
      en: "Overall avg",
      de: "Gesamtdurchschnitt",
    },
    durationSupply: {
      pt: "Supply",
      en: "Supply",
      de: "Supply",
    },
    avgSupply: {
      pt: "Média Supply",
      en: "Supply avg",
      de: "Supply-Durchschnitt",
    },
    durationQRA: {
      pt: "QRA",
      en: "QRA",
      de: "QRA",
    },
    avgQRA: {
      pt: "Média QRA",
      en: "QRA avg",
      de: "QRA-Durchschnitt",
    },
    servicesSupply: {
      pt: "Supply",
      en: "Supply",
      de: "Supply",
    },
    servicesQRA: {
      pt: "QRA",
      en: "QRA",
      de: "QRA",
    },
    customersSupply: {
      pt: "Supply",
      en: "Supply",
      de: "Supply",
    },
    customersQRA: {
      pt: "QRA",
      en: "QRA",
      de: "QRA",
    },
  },
  preferences: {
    title: {
      pt: "Preferências",
      en: "Preferences",
      de: "Einstellungen",
    },
    description: {
      pt: "Aqui você pode controlar o funcionamento da plataforma para a sua conta.",
      en: "Here you can control how the platform works for your account.",
      de: "Hier können Sie steuern, wie die Plattform für Ihr Konto funktioniert.",
    },
    tooltip: {
      pt: "Posicione o mouse sobre o [?] e veja a descrição de cada notificação.",
      en: "Hover over the [?] to see a description of each notification.",
      de: "Fahren Sie mit der Maus über [?], um die Beschreibung jeder Benachrichtigung zu sehen.",
    },

    resetPreferencesBtn: {
      pt: "Redefinir preferências",
      en: "Reset Preferences",
      de: "Einstellungen zurücksetzen",
    },
    saveBtn: {
      pt: "Salvar",
      en: "Save",
      de: "Speichern",
    },
    saving: {
      pt: "Salvando...",
      en: "Saving...",
      de: "Speichert...",
    },
    toastPreferencesReset: {
      pt: "Preferências redefinidas para o padrão, atualize a página",
      en: "Preferences reset to default, please refresh the page",
      de: "Einstellungen auf Standard zurückgesetzt, bitte Seite neu laden",
    },
    toastPreferencesSaved: {
      pt: "Preferências salvas, atualize a página",
      en: "Preferences saved, please refresh the page",
      de: "Einstellungen gespeichert, bitte Seite neu laden",
    },
    tableHeaderEvent: {
      pt: "Evento",
      en: "Event",
      de: "Ereignis",
    },
    tableHeaderStatus: {
      pt: "Status",
      en: "Status",
      de: "Status",
    },
    notificationAlwaysDefault: {
      pt: "Esta notificação sempre está {status} por padrão.",
      en: "This notification is always {status} by default.",
      de: "Diese Benachrichtigung ist standardmäßig immer {status}.",
    },
    statusActivated: {
      pt: "Sempre ativada por padrão",
      en: "Always activated by default",
      de: "Immer standardmäßig aktiviert",
    },
    statusDeactivated: {
      pt: "Sempre desativada por padrão",
      en: "Always deactivated by default",
      de: "Immer standardmäßig deaktiviert",
    },
  },

  responsibles: {
    title: {
      pt: "Responsáveis Cadastrados",
      en: "Registered Responsibles",
      de: "Registrierte Verantwortliche",
    },
    description: {
      pt: "Veja quem pode responder tickets",
      en: "See who can respond to tickets",
      de: "Sehen Sie, wer Tickets beantworten kann",
    },
  },

  changepw: {
    title: {
      pt: "Mudar senha",
      en: "Change Password",
      de: "Passwort ändern",
    },
    description: {
      pt: "",
      en: "",
      de: "",
    },
    invalidEmail: {
      pt: "Informe um email válido",
      en: "Please provide a valid email",
      de: "Bitte geben Sie eine gültige E-Mail-Adresse an",
    },
    passwordMinLength: {
      pt: "A senha deve conter pelo menos 8 caracteres",
      en: "Password must be at least 8 characters long",
      de: "Das Passwort muss mindestens 8 Zeichen lang sein",
    },
    passwordNotStrong: {
      pt: "A senha não é forte o suficiente",
      en: "Password is not strong enough",
      de: "Das Passwort ist nicht stark genug",
    },
    oldPasswordIncorrect: {
      pt: "Senha antiga está incorreta",
      en: "Old password is incorrect",
      de: "Altes Passwort ist falsch",
    },
    updatePasswordError: {
      pt: "Erro ao atualizar senha, tente novamente mais tarde.",
      en: "Error updating password, please try again later.",
      de: "Fehler beim Aktualisieren des Passworts, bitte versuchen Sie es später erneut.",
    },
    emailResetTitle: {
      pt: "Senha alterada",
      en: "Password changed",
      de: "Passwort geändert",
    },
    emailResetSubject: {
      pt: "Senha alterada @ Customer Service Desk",
      en: "Password changed @ Customer Service Desk",
      de: "Passwort geändert @ Customer Service Desk",
    },
    emailResetGreeting: {
      pt: "Olá",
      en: "Hello",
      de: "Hallo",
    },
    emailResetMessage: {
      pt: "Sua senha foi alterada no Customer Service Desk.",
      en: "Your password has been changed in the Customer Service Desk.",
      de: "Ihr Passwort wurde im Customer Service Desk geändert.",
    },
    emailResetWarning: {
      pt: "Se você não solicitou essa alteração, fale IMEDIATAMENTE com o(a) Administrador(a) do sistema!",
      en: "If you did not request this change, contact the system administrator IMMEDIATELY!",
      de: "Wenn Sie diese Änderung nicht angefordert haben, kontaktieren Sie SOFORT den Systemadministrator!",
    },
    oldPw: {
      pt: "Senha antiga",
      en: "Old password",
      de: "Altes Passwort",
    },
    newPw: {
      pt: "Nova senha",
      en: "New password",
      de: "Neues Passwort",
    },
    confirmNewPw: {
      pt: "Confirme a nova senha",
      en: "Confirm new password",
      de: "Neues Passwort bestätigen",
    },
    requirements: {
      pt: `<b className="text-lg">A senha deve conter:</b>
          <li>• Pelo menos 8 caracteres, dos quais:</li>
          <li>• Uma letra maiúscula</li>
          <li>• Uma letra minúscula</li>
          <li>• Um número</li>
          <li>• Um caracter especial dos seguintes: #?!@$%^&*- </li>
        `,
      en: `<b className="text-lg">The password must contain:</b>
          <li>• At least 8 characters, including:</li>
          <li>• One uppercase letter</li>
          <li>• One lowercase letter</li>
          <li>• One number</li>
          <li>• One special character from the following: #?!@$%^&*-</li>
        `,
      de: `<b className="text-lg">Das Passwort muss enthalten:</b>
          <li>• Mindestens 8 Zeichen, darunter:</li>
          <li>• Einen Großbuchstaben</li>
          <li>• Einen Kleinbuchstaben</li>
          <li>• Eine Zahl</li>
          <li>• Ein Sonderzeichen aus folgendem: #?!@$%^&*-</li>
        `,
    },
  },

  admin: {
    title: {
      pt: "Painel do Administrador",
      en: "Admin Panel",
      de: "Administrationsbereich",
    },
    description: {
      pt: "Gerencie usuários e serviços",
      en: "Manage users and services",
      de: "Verwalten Sie Benutzer und Dienste",
    },
    notAdminMessage: {
      pt: "Parece que você não é um administrador cadastrado.",
      en: "It seems you are not a registered administrator.",
      de: "Es scheint, dass Sie kein registrierter Administrator sind.",
    },
    restricted: {
      pt: "Página restrita",
      en: "Restricted page",
      de: "Eingeschränkte Seite",
    },
    apiKeyLabel: {
      pt: "Sua API Key",
      en: "Your API Key",
      de: "Ihr API-Schlüssel",
    },
    placeholderApiKey: {
      pt: "API Key do Administrador",
      en: "Administrator API Key",
      de: "Administrator-API-Key",
    },
    invalidApiKey: {
      pt: "API Key inválida",
      en: "Invalid API Key",
      de: "Ungültiger API-Key",
    },
    // --- ADMIN APPROVALS ---
    adminApprovalSectionTitle: {
      pt: "Admins",
      en: "Admins",
      de: "Admins",
    },
    adminApprovalSectionDescription: {
      pt: "Gerencie solicitações de novos administradores com aprovação dupla.",
      en: "Manage new admin requests with two-person approval.",
      de: "Verwalten Sie neue Administratoranfragen mit Zwei-Personen-Freigabe.",
    },
    adminApprovalRequestTitle: {
      pt: "Criar solicitação de administrador",
      en: "Create admin request",
      de: "Administratoranfrage erstellen",
    },
    adminApprovalRequestDescription: {
      pt: "Envie o e-mail do usuário para gerar uma aprovação. Se já houver mais de um admin, a solicitação precisará de mais um aprovador.",
      en: "Send the user email to start the approval. When more than one admin exists, another approver must confirm.",
      de: "Senden Sie die E-Mail des Benutzers, um den Genehmigungsprozess zu starten. Wenn es mehr als einen Admin gibt, ist eine zweite Freigabe erforderlich.",
    },
    adminApprovalEmailLabel: {
      pt: "E-mail do novo admin",
      en: "New admin email",
      de: "E-Mail des neuen Admins",
    },
    adminApprovalEmailPlaceholder: {
      pt: "usuario@empresa.com",
      en: "user@company.com",
      de: "benutzer@firma.com",
    },
    adminApprovalRequestButton: {
      pt: "Solicitar novo admin",
      en: "Request new admin",
      de: "Neuen Admin anfragen",
    },
    adminApprovalCreated: {
      pt: "Admin criado e notificado por e-mail.",
      en: "Admin created and notified by email.",
      de: "Administrator erstellt und per E-Mail benachrichtigt.",
    },
    adminApprovalPendingSecond: {
      pt: "Solicitação registrada. Compartilhe o ID {approvalId} com outro admin para aprovar.",
      en: "Request recorded. Share approval ID {approvalId} with another admin to finish approval.",
      de: "Anfrage gespeichert. Teilen Sie die Genehmigungs-ID {approvalId} mit einem anderen Admin zur Freigabe.",
    },
    adminApprovalPendingId: {
      pt: "ID da aprovação:",
      en: "Approval ID:",
      de: "Genehmigungs-ID:",
    },
    adminApprovalCopyId: {
      pt: "Copiar ID",
      en: "Copy ID",
      de: "ID kopieren",
    },
    adminApprovalApproveTitle: {
      pt: "Aprovar solicitação",
      en: "Approve request",
      de: "Anfrage freigeben",
    },
    adminManage: {
      pt: "Gerencie admins",
      en: "Manage admins",
      de: "Administratoren verwalten",
    },
    adminApprovalApproveDescription: {
      pt: "Use o ID de aprovação para confirmar um novo admin.",
      en: "Use the approval ID to validate a new admin.",
      de: "Verwenden Sie die Genehmigungs-ID, um einen neuen Admin zu bestätigen.",
    },
    adminApprovalIdLabel: {
      pt: "ID da aprovação",
      en: "Approval ID",
      de: "Genehmigungs-ID",
    },
    adminApprovalIdPlaceholder: {
      pt: "Digite o ID recebido",
      en: "Enter the approval ID",
      de: "Genehmigungs-ID eingeben",
    },
    adminApprovalApproveButton: {
      pt: "Aprovar admin",
      en: "Approve admin",
      de: "Admin freigeben",
    },
    adminApprovalApproved: {
      pt: "Admin aprovado e chave enviada por e-mail.",
      en: "Admin approved and welcome email sent.",
      de: "Admin freigegeben und Willkommens-E-Mail gesendet.",
    },
    adminApprovalRecorded: {
      pt: "Aprovação registrada. Ainda aguardando outra confirmação.",
      en: "Approval recorded. Waiting for another confirmation.",
      de: "Freigabe gespeichert. Wartet auf eine weitere Bestätigung.",
    },
    adminApprovalUnexpectedError: {
      pt: "Não foi possível processar a aprovação. Tente novamente.",
      en: "We couldn't process the approval. Try again.",
      de: "Genehmigung konnte nicht verarbeitet werden. Bitte erneut versuchen.",
    },
    adminApprovalCopied: {
      pt: "ID copiado!",
      en: "Approval ID copied!",
      de: "ID kopiert!",
    },
    // --- USERS ---
    usersTitle: {
      pt: "Usuários",
      en: "Users",
      de: "Benutzer",
    },
    internalTab: {
      pt: "Interno",
      en: "Internal",
      de: "Intern",
    },
    externalTab: {
      pt: "Externo",
      en: "External",
      de: "Extern",
    },
    newUserPwLabel: {
      pt: "Senha temporária",
      en: "Temporary Password",
      de: "Temporäres passwort",
    },
    generateTempPassword: {
      pt: "Gerar senha aleatória",
      en: "Generate random password",
      de: "Zufälliges Passwort generieren",
    },

    // --- EXTERNAL USERS ---
    externalCompTitle: {
      pt: "Cadastrar novo Usuário Externo (Cliente)",
      en: "Register New External User (Client)",
      de: "Neuen externen Benutzer (Kunde) registrieren",
    },
    externalCompDescription: {
      pt: "Este usuário deve estar associado a apenas um Gerente de Vendas. Este Gerente será responsável por processar as demandas dos clientes.",
      en: "This user must be associated with exactly one Sales Manager. That manager will be responsible for handling the clients’ requests.",
      de: "Dieser Benutzer muss genau einem Vertriebsleiter zugeordnet sein. Dieser ist für die Bearbeitung der Kundenanfragen verantwortlich.",
    },
    externalLabelNewUserName: {
      pt: "Nome do Usuário/Cliente",
      en: "User/Client Name",
      de: "Benutzer-/Kundenname",
    },
    manageExternalUsers: {
      pt: "Gerencie os Usuários Externos",
      en: "Manage External Users",
      de: "Externe Benutzer verwalten",
    },
    creatingExternalUser: {
      pt: "Criando usuário externo...",
      en: "Creating external user...",
      de: "Externer Benutzer wird erstellt...",
    },
    createExternalUser: {
      pt: "Criar novo Usuário Externo",
      en: "Create New External User",
      de: "Neuen externen Benutzer erstellen",
    },
    externalLabelSalesManager: {
      pt: "Gerente de Vendas",
      en: "Sales Manager",
      de: "Vertriebsleiter",
    },
    labelNewUserEmail: {
      pt: "E-mail do Usuário/Cliente",
      en: "User/Client Email",
      de: "Benutzer-/Kunden-E-Mail",
    },

    // --- INTERNAL USERS ---
    newInternalUserDescription: {
      pt: "Lembre-se, um usuário ainda terá acesso à plataforma, mas NÃO pode ser responsável por Tickets",
      en: "Remember, a user will still have access to the platform, but CANNOT be responsible for Tickets",
      de: "Denken Sie daran, ein Benutzer hat weiterhin Zugriff auf die Plattform, darf jedoch NICHT für Tickets verantwortlich sein",
    },
    newInternalUserTitle: {
      pt: "Novo Usuário Interno",
      en: "New Internal User",
      de: "Neuer interner Benutzer",
    },
    newUserNameLabel: {
      pt: "Nome do Usuário",
      en: "User Name",
      de: "Benutzername",
    },
    newUserEmailLabel: {
      pt: "E-mail do Usuário",
      en: "User Email",
      de: "Benutzer-E-Mail",
    },
    manageUsers: {
      pt: "Gerencie os Usuários",
      en: "Manage Users",
      de: "Benutzer verwalten",
    },
    creatingUser: {
      pt: "Criando usuário...",
      en: "Creating user...",
      de: "Benutzer wird erstellt...",
    },
    createUser: {
      pt: "Criar novo Usuário",
      en: "Create new User",
      de: "Neuen Benutzer erstellen",
    },
  },

  external: {
    title: {
      pt: "Gerenciamento de Usuários Externos",
      en: "External User Management",
      de: "Verwaltung externer Benutzer",
    },
    description: {
      pt: "Gerencie Usuários Externos.",
      en: "Manage external users.",
      de: "Verwalten Sie externe Benutzer.",
    },
  },

  adminResp: {
    title: {
      pt: "Gerenciador de Responsáveis",
      en: "Responsible Parties Manager",
      de: "Verantwortlichen-Verwaltung",
    },
    description: {
      pt: "Gerencie Responsáveis. Se você excluir um responsável, ele continuará existindo como usuário.",
      en: "Manage responsible parties. If you delete a responsible party, they will continue to exist as a user.",
      de: "Verwalten Sie Verantwortliche. Wenn Sie einen Verantwortlichen löschen, bleibt er als Benutzer erhalten.",
    },
    adminApiKeyLabel: {
      pt: "API Key do Admin",
      en: "Admin API Key",
      de: "Admin-API-Key",
    },
    invalidApiKey: {
      pt: "API Key inválida",
      en: "Invalid API Key",
      de: "Ungültiger API-Key",
    },
    tableHeaderId: {
      pt: "Id",
      en: "ID",
      de: "ID",
    },

    apiKeyLabel: {
      pt: "API Key",
      en: "API Key",
      de: "API-Key",
    },
    editLoading: {
      pt: "Editando user...",
      en: "Editing user...",
      de: "Benutzer wird bearbeitet...",
    },
    editRespDialogTitle: {
      pt: "Editar {name} - {email}",
      en: "Edit {name} - {email}",
      de: "Bearbeite {name} – {email}",
    },
    tableHeaderActions: {
      pt: "Ações",
      en: "Actions",
      de: "Aktionen",
    },
  },

  adminServices: {
    title: {
      pt: "Gerenciador de Serviços",
      en: "Services Manager",
      de: "Service-Manager",
    },
    description: {
      pt: "Gerencie Serviços. Se você atualizar um serviço, ele irá atualizar a tabela dos SLAs.",
      en: "Manage services. If you update a service, it will update the SLAs table.",
      de: "Verwalten Sie Services. Wenn Sie einen Service aktualisieren, wird die SLA-Tabelle aktualisiert.",
    },
    editServiceSuccess: {
      pt: "Serviço {service} editado",
      en: "Service {service} edited",
      de: "Service {service} bearbeitet",
    },
    editServiceDialogTitle: {
      pt: "Editar {service}",
      en: "Edit {service}",
      de: "Bearbeite {service}",
    },
    labelClusterArea: {
      pt: "Cluster/Área",
      en: "Cluster/Department",
      de: "Cluster/Bereich",
    },
    labelScope: {
      pt: "Escopo",
      en: "Scope",
      de: "Umfang",
    },
    editServiceLoading: {
      pt: "Editando serviço...",
      en: "Editing service...",
      de: "Service wird bearbeitet...",
    },
    adminApiKeyLabel: {
      pt: "API Key do Admin",
      en: "Admin API Key",
      de: "Admin-API-Key",
    },
    invalidApiKey: {
      pt: "API Key inválida",
      en: "Invalid API Key",
      de: "Ungültiger API-Key",
    },
    tableHeaderService: {
      pt: "Serviço",
      en: "Service",
      de: "Service",
    },
    tableHeaderArea: {
      pt: "Área",
      en: "Department",
      de: "Bereich",
    },
    tableHeaderScope: {
      pt: "Escopo",
      en: "Scope",
      de: "Umfang",
    },
    tableHeaderActions: {
      pt: "Ações",
      en: "Actions",
      de: "Aktionen",
    },
  },

  adminSla: {
    title: {
      pt: "Gerenciador de Serviços e SLAs",
      en: "Services and SLAs Manager",
      de: "Service- und SLA-Manager",
    },
    description: {
      pt: "Gerencie os SLAs e responsáveis deste serviço",
      en: "Manage SLAs and responsible parties for this service",
      de: "Verwalten Sie SLAs und Verantwortliche für diesen Service",
    },
    statusTitle: {
      pt: "Status de cadastro de SLAs",
      en: "SLA Registration Status",
      de: "SLA-Registrierungsstatus",
    },
    statusDescription: {
      pt: "Para cadastrar ou editar um SLA, clique no X ou no ✓.",
      en: "To register or edit an SLA, click on the X or the ✓.",
      de: "Zum Registrieren oder Bearbeiten eines SLA klicken Sie auf das X oder ✓.",
    },
    tableHeaderService: {
      pt: "Serviço",
      en: "Service",
      de: "Service",
    },
    tableHeaderHigh: {
      pt: "Alta",
      en: "High",
      de: "Hoch",
    },
    tableHeaderMedium: {
      pt: "Média",
      en: "Medium",
      de: "Mittel",
    },
    tableHeaderLow: {
      pt: "Baixa",
      en: "Low",
      de: "Niedrig",
    },
    manageSla: {
      pt: "Gerencie um SLA",
      en: "Manage an SLA",
      de: "SLA verwalten",
    },
    invalidParams: {
      pt: "Página indisponível. Para editar um SLA, você precisa informar o serviço E a prioridade!",
      en: "Page unavailable. To edit an SLA, you must specify the service AND the priority!",
      de: "Seite nicht verfügbar. Zum Bearbeiten eines SLA müssen Sie Service UND Priorität angeben!",
    },
    invalidService: {
      pt: "Página indisponível. Para editar um SLA, você precisa informar um serviço válido!",
      en: "Page unavailable. To edit an SLA, you must specify a valid service!",
      de: "Seite nicht verfügbar. Zum Bearbeiten eines SLA müssen Sie einen gültigen Service angeben!",
    },
    creatingSla: {
      pt: "Criando o SLA para o serviço {service} na prioridade {priority}",
      en: "Creating the SLA for service {service} at priority {priority}",
      de: "Erstelle SLA für Service {service} mit Priorität {priority}",
    },
    editOrDeleteSla: {
      pt: "Edite ou exclua o serviço {service} na prioridade {priority}",
      en: "Edit or delete the service {service} at priority {priority}",
      de: "Bearbeiten oder Löschen Sie Service {service} mit Priorität {priority}",
    },
    backToAdmin: {
      pt: "Voltar para o Painel do Administrador",
      en: "Back to Admin Dashboard",
      de: "Zurück zum Admin-Dashboard",
    },
    editSlaTitle: {
      pt: "Editar o SLA para o serviço",
      en: "Edit SLA for service",
      de: "SLA für Service bearbeiten",
    },
    labelService: {
      pt: "Serviço",
      en: "Service",
      de: "Service",
    },
    labelPriority: {
      pt: "Prioridade",
      en: "Priority",
      de: "Priorität",
    },
    labelSla: {
      pt: "SLA",
      en: "SLA",
      de: "SLA",
    },
    hours: {
      pt: "horas",
      en: "hours",
      de: "Stunden",
    },
    labelResponsible: {
      pt: "Responsável",
      en: "Responsible",
      de: "Verantwortlicher",
    },
    placeholderSelectResponsible: {
      pt: "Selecione um responsável",
      en: "Select a responsible party",
      de: "Verantwortlichen auswählen",
    },
    saveChanges: {
      pt: "Salvar alterações",
      en: "Save changes",
      de: "Änderungen speichern",
    },
    saving: {
      pt: "Salvando...",
      en: "Saving...",
      de: "Speichert...",
    },
    inactivateSlaTitle: {
      pt: "Inativar o SLA",
      en: "Deactivate SLA",
      de: "SLA deaktivieren",
    },
    confirmInactivateAction: {
      pt: "Digite 'inativar' para confirmar esta ação",
      en: "Type 'inativar' to confirm this action",
      de: "Geben Sie 'inativar' ein, um diese Aktion zu bestätigen",
    },
    inactivating: {
      pt: "Inativando...",
      en: "Deactivating...",
      de: "Deaktiviere...",
    },
    confirmDeactivateButton: {
      pt: "Sim, inative este SLA",
      en: "Yes, deactivate this SLA",
      de: "Ja, dieses SLA deaktivieren",
    },
    statusHighlightNote: {
      pt: "Combinações sem SLA cadastrado ficam destacadas em vermelho.",
      en: "Combinations without a registered SLA are highlighted in red.",
      de: "Kombinationen ohne registriertes SLA sind rot hervorgehoben.",
    },
    editModeTitle: {
      pt: "Modo de edição dos SLAs",
      en: "SLA Edit Mode",
      de: "SLA-Bearbeitungsmodus",
    },
    editModeDescription: {
      pt: "Alterne entre o fluxo atual por combinação específica ou a nova edição em tabela.",
      en: "Switch between the current per-combination flow or the new table edit.",
      de: "Wechseln Sie zwischen dem aktuellen Einzelkombinationsfluss oder der neuen Tabellenbearbeitung.",
    },
    editOneByone: {
      pt: "Editar um por vez",
      en: "Edit one at a time",
      de: "Einzeln bearbeiten",
    },
    editAsTable: {
      pt: "Editar como tabela",
      en: "Edit as table",
      de: "Als Tabelle bearbeiten",
    },
    editAsTableTitle: {
      pt: "Editar SLAs em tabela",
      en: "Edit SLAs in table",
      de: "SLAs in Tabelle bearbeiten",
    },
    editAsTableDescription: {
      pt: "Linhas destacadas indicam combinações de serviço e prioridade que ainda não possuem SLA cadastrado.",
      en: "Highlighted rows indicate service and priority combinations that don't have an SLA registered yet.",
      de: "Hervorgehobene Zeilen zeigen Service-Prioritäts-Kombinationen ohne registriertes SLA an.",
    },
    tableHeaderDefaultResponsible: {
      pt: "Responsável padrão",
      en: "Default Responsible",
      de: "Standard-Verantwortlicher",
    },
    singleModeNote: {
      pt: "No modo atual, use os ícones do painel acima para abrir a edição de um SLA por vez.",
      en: "In the current mode, use the panel icons above to open SLA editing one at a time.",
      de: "Verwenden Sie im aktuellen Modus die Symbole im obigen Panel, um SLAs einzeln zu bearbeiten.",
    },
    createSla: {
      pt: "Criar",
      en: "Create",
      de: "Erstellen",
    },
    saveSla: {
      pt: "Salvar",
      en: "Save",
      de: "Speichern",
    },
  },

  adminUser: {
    title: {
      pt: "",
      en: "",
      de: "",
    },
    description: {
      pt: "",
      en: "",
      de: "",
    },
    editUserSuccess: {
      pt: "Usuário {email} editado",
      en: "User {email} edited",
      de: "Benutzer {email} bearbeitet",
    },
    editUserDialogTitle: {
      pt: "Editar {name} - {email}",
      en: "Edit {name} - {email}",
      de: "Bearbeite {name} – {email}",
    },
    editLoading: {
      pt: "Editando user...",
      en: "Editing user...",
      de: "Benutzer wird bearbeitet...",
    },
    tableHeaderId: {
      pt: "Id",
      en: "ID",
      de: "ID",
    },
    tableHeaderEmail: {
      pt: "E-mail",
      en: "Email",
      de: "E-Mail",
    },
    tableHeaderActions: {
      pt: "Ações",
      en: "Actions",
      de: "Aktionen",
    },
    badgeExternal: {
      pt: "Externo",
      en: "External",
      de: "Extern",
    },
  },

  resetpw: {
    title: {
      pt: "Redefinição de Senha",
      en: "Password Reset",
      de: "Passwort zurücksetzen",
    },
    description: {
      pt: "Gerencie um SLA",
      en: "Manage an SLA",
      de: "Verwalten Sie ein SLA",
    },
    missingEmail: {
      pt: "Página indisponível. Para redefinir uma senha, você precisa informar um email",
      en: "Page unavailable. To reset a password, you must provide an email",
      de: "Seite nicht verfügbar. Um ein Passwort zurückzusetzen, müssen Sie eine E-Mail angeben",
    },
    compTitle: {
      pt: "Redefinir senha",
      en: "Reset Password",
      de: "Passwort zurücksetzen",
    },
    labelEmailToReset: {
      pt: "E-mail do usuário",
      en: "User Email",
      de: "Benutzer-E-Mail",
    },
    labelApiKey: {
      pt: "API Key do admin",
      en: "Admin API Key",
      de: "Admin-API-Key",
    },
    resetBtn: {
      pt: "Redefinir senha",
      en: "Reset Password",
      de: "Passwort zurücksetzen",
    },
    resetLoading: {
      pt: "Refinindo...",
      en: "Resetting...",
      de: "Wird zurückgesetzt...",
    },
    invalidApiKeyAction: {
      pt: "API Key inválida",
      en: "Invalid API Key",
      de: "Ungültiger API-Key",
    },
    resetPwEmailTitle: {
      pt: "Senha redefinida",
      en: "Password Reset",
      de: "Passwort zurückgesetzt",
    },
    resetPwEmailSubject: {
      pt: "Senha redefinida @ Customer Service Desk",
      en: "Password Reset @ Customer Service Desk",
      de: "Passwort zurückgesetzt @ Customer Service Desk",
    },
    resetPwEmailGreeting: {
      pt: "Olá, {email}",
      en: "Hello, {email}",
      de: "Hallo, {email}",
    },
    resetPwRequestedByAdmin: {
      pt: "Um(a) Administrador(a) do sistema solicitou reset da sua senha!",
      en: "A system administrator has requested a password reset!",
      de: "Ein Systemadministrator hat eine Passwortzurücksetzung angefordert!",
    },
    resetPwTemporaryPassword: {
      pt: "Sua senha temporária (troque no próximo acesso à plataforma!): {password}",
      en: "Your temporary password (change on next platform access!): {password}",
      de: "Ihr temporäres Passwort (ändern Sie es beim nächsten Zugriff auf die Plattform!): {password}",
    },
    resetPwOkMessage: {
      pt: "OK",
      en: "OK",
      de: "OK",
    },
    resetPwFailedMessage: {
      pt: "Falha ao atualizar a senha",
      en: "Failed to update password",
      de: "Passwort konnte nicht aktualisiert werden",
    },
    resetPwValidationFailed: {
      pt: "Falha na validação",
      en: "Validation failed",
      de: "Validierung fehlgeschlagen",
    },
  },

  login: {
    title: {
      pt: "Bem-vindo de volta",
      en: "Welcome Back",
      de: "Willkommen zurück",
    },
    description: {
      pt: "Informe suas credenciais para entrar no Customer Service Desk",
      en: "Enter your credentials to log in to the Customer Service Desk",
      de: "Geben Sie Ihre Zugangsdaten ein, um sich im Customer Service Desk anzumelden",
    },
    logoAlt: {
      pt: "Logotipo da Gelita",
      en: "Gelita Logo",
      de: "Gelita-Logo",
    },
    loginError: {
      pt: "Login não deu certo :( revise suas credenciais",
      en: "Login failed :( please check your credentials",
      de: "Anmeldung fehlgeschlagen :( bitte überprüfen Sie Ihre Zugangsdaten",
    },
    labelEmail: {
      pt: "Email",
      en: "Email",
      de: "E-Mail",
    },
    labelPassword: {
      pt: "Senha",
      en: "Password",
      de: "Passwort",
    },
    placeholderEmail: {
      pt: "email@exemplo.com",
      en: "email@example.com",
      de: "email@beispiel.com",
    },
    placeholderPassword: {
      pt: "••••••••",
      en: "••••••••",
      de: "••••••••",
    },
    loginButton: {
      pt: "Entrar",
      en: "Login",
      de: "Anmelden",
    },
    loadingText: {
      pt: "Aguarde...",
      en: "Loading...",
      de: "Lädt...",
    },
    invalidCredentials: {
      pt: "Credenciais inválidas",
      en: "Invalid credentials",
      de: "Ungültige Anmeldedaten",
    },
  },

  forgotpw: {
    title: {
      pt: "Recuperar senha",
      en: "Retrieve password",
      de: "Passwort wiederherstellen",
    },
    description: {
      pt: "Siga as etapas para solicitar troca de senha.\n1) Informe seu email.\n2) Confirme o código enviado por e-mail.\n3) Crie uma nova senha.\nPor fim, um admin irá aprovar/rejeitar sua solicitação.",
      en: "Follow these steps to request a password change.\n1) Enter your email address.\n2) Confirm the code sent to your email.\n3) Create a new password.\nFinally, an admin will approve or reject your request.",
      de: "Folgen Sie diesen Schritten, um eine Passwortänderung zu beantragen.\n1) Geben Sie Ihre E-Mail-Adresse ein.\n2) Bestätigen Sie den per E-Mail erhaltenen Code.\n3) Erstellen Sie ein neues Passwort.\nEin Administrator wird Ihren Antrag anschließend genehmigen oder ablehnen.",
    },
    link: {
      pt: "Esqueci minha senha",
      en: "Forgot my password",
      de: "Passwort vergessen",
    },
    emailLabel: {
      pt: "Email",
      en: "Email",
      de: "E-Mail",
    },
    emailButton: {
      pt: "Enviar código para o meu email",
      en: "Send code to my email",
      de: "Code an meine email",
    },
    otpLabel: {
      pt: "Código Único",
      en: "OTP code",
      de: "OTP-Code",
    },
    otpButton: {
      pt: "Validar código",
      en: "Validate code",
      de: "Code validieren",
    },
    newPwLabel: {
      pt: "Nova senha",
      en: "New password",
      de: "Neues Passwort",
    },
    saveButton: {
      pt: "Solicitar alteração",
      en: "Request change",
      de: "Änderung anfordern",
    },
    emailNotRegistered: {
      pt: "Email não cadastrado",
      en: "Email not registered",
      de: "E-Mail nicht registriert",
    },
    emailSent: {
      pt: "Se o email estiver cadastrado, um OTP foi enviado.",
      en: "If the email is registered, an OTP has been sent.",
      de: "Falls die E-Mail registriert ist, wurde ein OTP gesendet.",
    },
    otpInvalid: {
      pt: "Código inválido ou expirado",
      en: "Invalid or expired code",
      de: "Ungültiger oder abgelaufener Code",
    },
    pwWeak: {
      pt: "A senha deve conter maiúscula, minúscula, número e caractere especial",
      en: "Password must contain uppercase, lowercase, number and special character",
      de: "Passwort muss Groß-/Kleinbuchstaben, Zahl und Sonderzeichen enthalten",
    },
    adminPending: {
      pt: "Solicitação enviada. Um administrador foi informado e deve aprovar ou negar por e-mail.",
      en: "Request sent. An admin was informed and must approve or deny by email.",
      de: "Anfrage gesendet. Ein Admin wurde informiert und muss per E-Mail genehmigen oder ablehnen.",
    },
    decisionDone: {
      pt: "Decisão registrada",
      en: "Decision recorded",
      de: "Entscheidung gespeichert",
    },
  },

  logout: {
    title: {
      pt: "Efetuando logout...",
      en: "Logging you out...",
      de: "Sie werden abgemeldet...",
    },
    description: {
      pt: "Por favor, aguarde, você será redirecionado em breve.",
      en: "Please wait, you will be redirected shortly.",
      de: "Bitte warten Sie, Sie werden in Kürze weitergeleitet.",
    },
    sure: {
      pt: "Tem certeza?",
      en: "Are you sure?",
      de: "Sind Sie sicher?",
    },
  },

  ticketDataTable: {
    title: {
      pt: "",
      en: "",
      de: "",
    },
    description: {
      pt: "",
      en: "",
      de: "",
    },

    serviceTeamCol: {
      pt: "Área do Serviço",
      en: "Service Department",
      de: "Service-Bereich",
    },
    responsibleTeamCol: {
      pt: "Área do Responsável",
      en: "Responsible Department",
      de: "Verantwortlicher Bereich",
    },
    creatorIdCol: {
      pt: "Solicitado por",
      en: "Requested by",
      de: "Angefragt von",
    },
    viewButton: {
      pt: "View",
      en: "View",
      de: "Anzeigen",
    },
    viewText: {
      pt: "Selecionar colunas",
      en: "Select columns",
      de: "Spalten auswählen",
    },
    noResult: {
      pt: "Nenhum resultado",
      en: "No results",
      de: "Keine Ergebnisse",
    },
    ticketsPerPageLabel: {
      pt: "Tickets/pág.",
      en: "Tickets per page",
      de: "Tickets pro Seite",
    },
    goToFirstPage: {
      pt: "Ir para primeira página",
      en: "Go to first page",
      de: "Zur ersten Seite",
    },
    goToPreviousPage: {
      pt: "Ir para página anterior",
      en: "Go to previous page",
      de: "Zur vorherigen Seite",
    },
    goToNextPage: {
      pt: "Ir para próxima página",
      en: "Go to next page",
      de: "Zur nächsten Seite",
    },
    goToLastPage: {
      pt: "Ir para última página",
      en: "Go to last page",
      de: "Zur letzten Seite",
    },
    pageInfo: {
      pt: "Página {page} de {pageCount}",
      en: "Page {page} of {pageCount}",
      de: "Seite {page} von {pageCount}",
    },
    statusFilterTitle: {
      pt: "Status",
      en: "Status",
      de: "Status",
    },
    priorityFilterTitle: {
      pt: "Prioridade",
      en: "Priority",
      de: "Priorität",
    },
    responsibleFilterTitle: {
      pt: "Responsável",
      en: "Responsible",
      de: "Verantwortlicher",
    },
    serviceAreaFilterTitle: {
      pt: "Área Serviço",
      en: "Service Department",
      de: "Service-Bereich",
    },
    responsibleAreaFilterTitle: {
      pt: "Área Resp.",
      en: "Resp. Department",
      de: "Verantwortlicher Bereich",
    },
    serviceFilterTitle: {
      pt: "Serviço",
      en: "Service",
      de: "Dienst",
    },
    creatorIdFilterTitle: {
      pt: "Solicitado por",
      en: "Requested by",
      de: "Angefragt von",
    },
    lastCommentFilterTitle: {
      pt: "Últ. comentário",
      en: "Last comment",
      de: "Letz. Kommentar",
    },
    filterTitleLabel: {
      pt: "Filtrar Título",
      en: "Filter by Title",
      de: "Nach Titel filtern",
    },
    filterClientLabel: {
      pt: "Filtrar Cliente",
      en: "Filter by Customer",
      de: "Nach Kunde filtern",
    },
    selectedLabel: {
      pt: "selecionados",
      en: "selected",
      de: "ausgewählt",
    },
    selectedLabelOne: {
      pt: "selecionado",
      en: "selected",
      de: "ausgewählt",
    },
    filterPOLabel: {
      pt: "Filtrar PO",
      en: "Filter by PO",
      de: "Nach PO filtern",
    },
    filterTitlePlaceholder: {
      pt: "Filtrar por Título",
      en: "Filter by Title",
      de: "Nach Titel filtern",
    },
    filterClientPlaceholder: {
      pt: "Filtrar por Cliente",
      en: "Filter by Customer",
      de: "Nach Kunde filtern",
    },
    filterPOPlaceholder: {
      pt: "Filtrar por PO",
      en: "Filter by PO",
      de: "Nach PO filtern",
    },
  },

  common: {
    customerLabel: {
      pt: "Nome/Núm. do Cliente",
      en: "Customer Name/Number",
      de: "Name/Kundennummer",
    },
    customerPlaceholder: {
      pt: "Nome ou Número do Cliente",
      en: "Customer Name or Number",
      de: "Name oder Kundennummer",
    },
    service: {
      pt: "Serviço",
      en: "Service",
      de: "Dienstleistung",
    },
    lastComment: {
      pt: "Últ. comentário",
      en: "Last comment",
      de: "Letz. Kommentar",
    },
    area: {
      pt: "Área",
      en: "Department",
      de: "Bereich",
    },
    scope: {
      pt: "Escopo",
      en: "Scope",
      de: "Umfang",
    },
    priority: {
      pt: "Prioridade",
      en: "Priority",
      de: "Priorität",
    },
    email: {
      pt: "E-mail",
      en: "E-mail",
      de: "E-mail",
    },
    status: {
      pt: "Status",
      en: "Status",
      de: "Status",
    },
    createdAt: {
      pt: "Criado em",
      en: "Created at",
      de: "Erstellt am",
    },
    sla: {
      pt: "SLA",
      en: "SLA",
      de: "SLA",
    },
    duration: {
      pt: "Duração",
      en: "Duration",
      de: "Dauer",
    },
    overdue: {
      pt: "Atraso",
      en: "Overdue",
      de: "Überfällig",
    },
    poNum: {
      pt: "PO",
      en: "PO",
      de: "PO",
    },
    customerId: {
      pt: "Cliente",
      en: "Customer",
      de: "Kunde",
    },
    title: {
      pt: "Título",
      en: "Title",
      de: "Titel",
    },
    currentResponsible: {
      pt: "Responsável",
      en: "Current Responsible",
      de: "Aktueller Verantwortlicher",
    },
    hours: {
      pt: "horas",
      en: "hours",
      de: "Stunden",
    },
    name: {
      pt: "Nome",
      en: "Name",
      de: "Name",
    },
    allScopes: { pt: "Tudo", en: "All", de: "Alle" },
    poPlaceholder: { pt: "Número da PO", en: "PO Number", de: "PO-Nummer" },
    cancel: {
      pt: "Cancelar",
      en: "Cancel",
      de: "Abbrechen",
    },
    edit: {
      pt: "Editar",
      en: "Edit",
      de: "Bearbeiten",
    },
    editTrigger: {
      pt: "Editar Ticket",
      en: "Edit Ticket",
      de: "Ticket bearbeiten",
    },
    editing: {
      pt: "Editando...",
      en: "Editing...",
      de: "Wird bearbeitet...",
    },
    commentPlaceholder: {
      pt: "Digite seu comentário",
      en: "Enter your comment",
      de: "Geben Sie Ihren Kommentar ein",
    },
    creator: {
      pt: "Solicitado por",
      en: "Requested by",
      de: "Angefragt von",
    },
    confirmPrompt: {
      pt: "Tem certeza?",
      en: "Are you sure?",
      de: "Sind Sie sicher?",
    },
    confirmYes: {
      pt: "Sim",
      en: "Yes",
      de: "Ja",
    },
    confirmNo: {
      pt: "Não",
      en: "No",
      de: "Nein",
    },
  },
  sendToBacklog: {
    description: {
      pt: "",
      en: "",
      de: "",
    },
    title: {
      pt: "",
      en: "",
      de: "",
    },
    selectDepartment: {
      pt: "Selecione uma área",
      en: "Select Backlog department",
      de: "Backlog-Bereich",
    },
    instruction: {
      pt: "Selecione a área do Backlog e o novo responsável pelo Ticket.",
      en: "Select the Backlog department and the new person responsible for the ticket.",
      de: "Wählen Sie den Backlog-Bereich und die neue für das Ticket verantwortliche Person aus.",
    },
    selectArea: {
      pt: "Selecione a área responsável",
      en: "Select the responsible department",
      de: "Wählen Sie den verantwortlichen Bereich",
    },
    selectResponsible: {
      pt: "Selecione a pessoa responsável (opcional)",
      en: "Select the responsible person (optional)",
      de: "Wählen Sie die verantwortliche Person (optional)",
    },
    addComment: {
      pt: "Adicione um comentário",
      en: "Add a comment",
      de: "Fügen Sie einen Kommentar hinzu",
    },
    alertSelectArea: {
      pt: "Informe a área responsável pelo Backlog",
      en: "Please specify the responsible Backlog area",
      de: "Bitte geben Sie den verantwortlichen Backlog-Bereich an",
    },
    sendButton: {
      pt: "Enviar para Backlog",
      en: "Send to Backlog",
      de: "In den Backlog senden",
    },
    updatingStatus: {
      pt: "Atualizando status...",
      en: "Updating status...",
      de: "Status wird aktualisiert...",
    },
  },
  status: {
    description: {
      pt: "",
      en: "",
      de: "",
    },
    title: {
      pt: "",
      en: "",
      de: "",
    },
    backlog: {
      pt: "Backlog",
      en: "Backlog",
      de: "Backlog",
    },
    pending: {
      pt: "Pendente",
      en: "Pending",
      de: "Ausstehend",
    },
    "in progress": {
      pt: "Em curso",
      en: "In Progress",
      de: "In Bearbeitung",
    },
    done: {
      pt: "Concluído",
      en: "Done",
      de: "Abgeschlossen",
    },
    canceled: {
      pt: "Cancelado",
      en: "Canceled",
      de: "Abgebrochen",
    },
    rejected: {
      pt: "Rejeitado",
      en: "Rejected",
      de: "Abgelehnt",
    },
  },
} as const;

// Função para pegar a tradução de uma página e propriedade
export function t(
  page: TPages,
  key: string,
  language: TLanguages | string = "en",
  params: Record<string, string> = {},
): string {
  if (!languages.includes(language)) return "";

  let text = pageTranslations[page]?.[key]?.[language as TLanguages] || "";
  for (const [k, v] of Object.entries(params)) {
    text = text.replaceAll(`{${k}}`, v);
  }
  return text;
}

export function translatePriority(
  priority: TPriority,
  language: TLanguages | string = "en",
) {
  return pageTranslations.priority[priority][language as TLanguages] || "";
}

export function translateNotification(page: TPages, lang: string | TLanguages) {
  return pageTranslations.navigation[page][lang as TLanguages] || "";
}
